import { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Award, 
  Wrench, 
  Users, 
  Star, 
  CheckCircle,
  Shield,
  Heart,
  Calendar,
  BookOpen,
  Home,
  GraduationCap,
  Trophy,
  Building2,
  Target,
  Clock,
  MessageCircle,
  UserCheck,
  Zap,
  Battery,
  Flame,
  Settings,
  TrendingUp
} from 'lucide-react';

const Zack = () => {
  // SEO meta tags setup
  useEffect(() => {
    
    const metaTags = [
      { name: 'description', content: 'Meet Zack, newest team member at Colorado First Plumbing. Water heater and boiler installation expert, Milwaukee Tool enthusiast, works 2 full-time jobs, brings 110% commitment to everything.' },
      { name: 'keywords', content: 'Zack Colorado First Plumbing, water heater installation expert, boiler installation specialist, Milwaukee Tools, dedicated plumber Colorado, hard worker Denver plumbing' },
      { property: 'og:title', content: 'Zack - Newest Team Member | Colorado First Plumbing' },
      { property: 'og:description', content: 'Newest team member with water heater and boiler installation expertise. Known for determination, commitment, and positive attitude.' },
      { property: 'og:type', content: 'profile' },
      { property: 'og:url', content: 'https://colorado-plumbing.com/team/zack' },
    ];
    
    metaTags.forEach(tag => {
      let metaElement = document.querySelector(`meta[${tag.name ? 'name' : 'property'}="${tag.name || tag.property}"]`);
      if (metaElement) {
        metaElement.setAttribute('content', tag.content);
      } else {
        metaElement = document.createElement('meta');
        if (tag.name) {
          metaElement.setAttribute('name', tag.name);
        } else {
          metaElement.setAttribute('property', tag.property);
        }
        metaElement.setAttribute('content', tag.content);
        document.head.appendChild(metaElement);
      }
    });

    // Structured data for Zack
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      'name': 'Zack',
      'jobTitle': 'Newest Team Member',
      'worksFor': {
        '@type': 'LocalBusiness',
        'name': 'Colorado First Plumbing and Sewer LLC',
        'url': 'https://colorado-plumbing.com'
      },
      'description': 'Newest team member with high-level experience in water heater and boiler installation. Known for determination, commitment, and bringing 110% to everything.',
      'knowsAbout': ['Water Heater Installation', 'Boiler Installation', 'Tool Expertise', 'Equipment Maintenance', 'Quality Installation'],
      'url': 'https://colorado-plumbing.com/team/zack'
    };

    let structuredDataScript = document.querySelector('#zack-structured-data');
    if (structuredDataScript) {
      structuredDataScript.textContent = JSON.stringify(structuredData);
    } else {
      structuredDataScript = document.createElement('script');
      structuredDataScript.setAttribute('id', 'zack-structured-data');
      structuredDataScript.setAttribute('type', 'application/ld+json');
      structuredDataScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(structuredDataScript);
    }

    return () => {
      const script = document.querySelector('#zack-structured-data');
      if (script) {
        document.head.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-light to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-56 pb-16 bg-gradient-to-br from-trust-deep to-authority-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-trust-deep/20 to-authority-navy/20"></div>
        <div className="container-custom relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-6">
                <Trophy className="w-8 h-8 text-white mr-3" />
                <span className="px-4 py-2 bg-white/20 rounded-full text-white font-medium text-sm border border-white/30">
                  110% Commitment
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                Zack
              </h1>
              <h2 className="text-2xl md:text-3xl text-white/90 font-semibold mb-6">
                Newest Team Member
              </h2>
              
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                High-level experience in water heater and boiler installation with exceptional determination and commitment. 
                Known for bringing the best attitude to the workplace every day and putting 110% into everything he does.
              </p>
              
              {/* Key Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20">
                  <div className="text-lg font-bold text-white">2 Full-Time</div>
                  <div className="text-sm text-white/80">Jobs</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20">
                  <div className="text-lg font-bold text-white">Milwaukee</div>
                  <div className="text-sm text-white/80">Tool Expert</div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                
                <a 
                  href="#experience" 
                  className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/20 transition-all duration-300 font-medium border border-white/30"
                >
                  <BookOpen size={16} className="mr-2" />
                  View Experience
                </a>
              </div>
            </div>
            
            {/* Team Photo */}
            <div className="flex justify-center">
              <div className="aspect-[4/3] w-full max-w-md rounded-xl overflow-hidden shadow-2xl border-4 border-white/30">
                <img 
                  src="/colorado-plumbing-uploads/team/zack.png" 
                  alt="Zack - Newest Team Member at Colorado First Plumbing" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Experience Section */}
      <section id="experience" className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-authority-navy mb-4">
              Installation Expertise & Work Ethic
            </h2>
            <p className="text-xl text-neutral-gray max-w-3xl mx-auto">
              Specialized expertise in water heater and boiler installations with an unmatched commitment to quality and excellence.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Current Role */}
            <div className="bg-trust-primary/10 rounded-xl p-8 border border-trust-primary/20">
              <div className="flex items-center mb-6">
                <Trophy className="w-8 h-8 text-trust-primary mr-3" />
                <div>
                  <h3 className="text-xl font-bold text-authority-navy">Newest Team Member</h3>
                  <p className="text-trust-primary font-medium">Colorado First Plumbing and Sewer LLC</p>
                </div>
              </div>
              
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">High-level water heater installation expertise</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">Boiler installation and service specialization</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">Brings 110% commitment to every project</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">Best attitude and positive energy daily</span>
                </li>
              </ul>
            </div>

            {/* Work Ethic & Dedication */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-trust-light to-white rounded-xl p-6 border border-trust-primary/20 shadow-md">
                <div className="flex items-center mb-4">
                  <Clock className="w-6 h-6 text-trust-primary mr-3" />
                  <div>
                    <h4 className="text-lg font-bold text-authority-navy">Exceptional Work Ethic</h4>
                    <p className="text-trust-primary font-medium">Dual Employment Commitment</p>
                  </div>
                </div>
                
                <p className="text-neutral-gray text-sm mb-3">
                  Works 2 full-time jobs demonstrating extraordinary dedication and time management skills.
                </p>
                
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-trust-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">Exceptional time management</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-trust-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">High energy and motivation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-trust-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">Reliable and dependable</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-authority-navy/10 to-white rounded-xl p-6 border border-authority-navy/20 shadow-md">
                <div className="flex items-center mb-4">
                  <Wrench className="w-6 h-6 text-authority-navy mr-3" />
                  <div>
                    <h4 className="text-lg font-bold text-authority-navy">Milwaukee Tool Expert</h4>
                    <p className="text-authority-navy font-medium">Comprehensive Tool Knowledge</p>
                  </div>
                </div>
                
                <p className="text-neutral-gray text-sm mb-3">
                  Owns almost every Milwaukee Tool known, demonstrating commitment to quality equipment and craftsmanship.
                </p>
                
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-authority-navy rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">Extensive tool collection</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-authority-navy rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">Professional equipment expertise</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-authority-navy rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">Quality-focused approach</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specializations Section */}
      <section className="py-16 bg-gradient-to-br from-neutral-light to-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-authority-navy mb-4">
              Technical Specializations & Skills
            </h2>
            <p className="text-xl text-neutral-gray max-w-2xl mx-auto">
              Specialized expertise in heating systems with focus on water heater and boiler installation excellence.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-trust-primary/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <Flame className="w-8 h-8 text-trust-primary mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">Water Heater Installation</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                High-level experience in water heater installation across all types and brands with focus on quality and efficiency.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Traditional tank water heaters
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Tankless water heater systems
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Gas and electric installations
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-authority-navy/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <Battery className="w-8 h-8 text-authority-navy mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">Boiler Installation</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Specialized expertise in boiler installation and service for residential and commercial heating systems.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Residential boiler systems
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Commercial boiler installation
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  System maintenance and repair
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-trust-primary/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <Wrench className="w-8 h-8 text-trust-primary mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">Professional Tools</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Comprehensive Milwaukee Tool collection ensuring the right tool for every job with professional quality results.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Power tools and equipment
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Specialized installation tools
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Quality measurement devices
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-success-primary/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <Settings className="w-8 h-8 text-success-primary mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">System Diagnostics</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Advanced diagnostic skills for troubleshooting heating system issues and ensuring optimal performance.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  System performance analysis
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Efficiency optimization
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Preventive maintenance
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-trust-deep/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <Target className="w-8 h-8 text-trust-deep mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">Quality Installation</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Commitment to quality installation practices ensuring long-lasting, efficient heating system performance.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Code compliance assurance
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Safety standard adherence
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Long-term reliability focus
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-authority-navy/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <TrendingUp className="w-8 h-8 text-authority-navy mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">Continuous Improvement</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Dedication to continuous learning and improvement in installation techniques and industry best practices.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Latest industry techniques
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Equipment knowledge updates
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Professional development
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Work Philosophy Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-authority-navy mb-4">
              Work Philosophy & Values
            </h2>
            <p className="text-xl text-neutral-gray max-w-2xl mx-auto">
              Driven by determination, commitment, and the belief that bringing 110% effort to everything creates exceptional results.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-trust-primary/10 to-white rounded-xl p-8 border border-trust-primary/20">
              <div className="flex items-center mb-6">
                <Trophy className="w-8 h-8 text-trust-primary mr-3" />
                <h3 className="text-xl font-bold text-authority-navy">Professional Excellence</h3>
              </div>
              
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Target className="w-5 h-5 text-trust-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">110% Commitment</div>
                    <div className="text-neutral-gray">Puts maximum effort into everything he does</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <TrendingUp className="w-5 h-5 text-trust-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">High Level of Determination</div>
                    <div className="text-neutral-gray">Persistent problem-solving and goal achievement</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <Star className="w-5 h-5 text-trust-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Best Attitude Daily</div>
                    <div className="text-neutral-gray">Brings positive energy to the workplace every day</div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-trust-light to-white rounded-xl p-8 border border-trust-primary/20">
              <div className="flex items-center mb-6">
                <Wrench className="w-8 h-8 text-trust-primary mr-3" />
                <h3 className="text-xl font-bold text-authority-navy">Technical Dedication</h3>
              </div>
              
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Wrench className="w-5 h-5 text-trust-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Tool Expertise</div>
                    <div className="text-neutral-gray">Comprehensive Milwaukee Tool collection and knowledge</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <Clock className="w-5 h-5 text-trust-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Time Management</div>
                    <div className="text-neutral-gray">Successfully manages 2 full-time job commitments</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <Shield className="w-5 h-5 text-trust-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Quality Focus</div>
                    <div className="text-neutral-gray">Dedicated to delivering professional-grade installations</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Zack Section */}
      <section className="py-16 bg-gradient-to-br from-trust-deep to-authority-navy text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Experience Dedicated Professional Service
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Work with Zack and the Colorado First Plumbing team for exceptional water heater and boiler installation services delivered with 110% commitment.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto">
            
            
          </div>
          
          {/* Excellence Badge */}
          <div className="mt-8 p-4 bg-white/10 rounded-xl border border-white/30 max-w-lg mx-auto">
            <div className="flex items-center justify-center mb-2">
              <Trophy className="w-5 h-5 text-white mr-2" />
              <span className="text-white font-semibold">110% Commitment to Excellence</span>
            </div>
            <p className="text-sm text-white/90">
              Water heater and boiler installation expertise with unmatched dedication.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Zack;